import React, {useEffect, useRef, useState} from "react";
import {Button, Input, Typography} from "antd";
import {ClockCircleOutlined} from "@ant-design/icons";
import {ThemeDefault} from "../Conf";
import {RsoCaptchaSmartLogin} from "./RsoCaptchaSmartLogin";
import {sendCode} from "../backend/UserBackend";
import i18next from "i18next";
import {CaptchaRule, getCaptchaProviderItems, getCaptchaRule, showMessage} from "../Setting";

const {Text} = Typography;

const toPersianDigits = (num) => {
  const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  return num.toString().replace(/\d/g, (x) => persianDigits[x]);
};

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
};

const RsoSendCodeOtp = ({
  value = "",
  onChange,
  length = 6,
  initialTimer = 60,
  onButtonClickArgs,
  application,
  method,
  countryCode,
  onSend,
  shouldReloadCaptcha,
  onComplete,
}) => {
  const [hasSentCode, setHasSentCode] = useState(false);

  const [isCaptchaFalse, setIsCaptchaFalse] = useState(0);
  const [buttonLeftTime, setButtonLeftTime] = useState(0);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaImg, setCaptchaImg] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showCaptcha, setShowCaptcha] = useState(true);
  const inputRefs = useRef([]);
  const themeData = application?.themeData ?? ThemeDefault;

  const handleCaptchaShow = () => {
    if (getCaptchaRule(application) === CaptchaRule.Never) {
      return false;
    }
    const captchaProviderItems = getCaptchaProviderItems(application);
    const alwaysProviderItems = captchaProviderItems.filter(providerItem => providerItem.rule === "Always");
    const dynamicProviderItems = captchaProviderItems.filter(providerItem => providerItem.rule === "Dynamic");
    const internetOnlyProviderItems = captchaProviderItems.filter(providerItem => providerItem.rule === "Internet-Only");

    // Select provider based on the active captcha rule, not fixed priority
    const captchaRule = getCaptchaRule(application);
    let provider = null;

    if (captchaRule === CaptchaRule.Always && alwaysProviderItems.length > 0) {
      provider = alwaysProviderItems[0].provider;
    } else if (captchaRule === CaptchaRule.Dynamic && dynamicProviderItems.length > 0) {
      provider = dynamicProviderItems[0].provider;
    } else if (captchaRule === CaptchaRule.InternetOnly && internetOnlyProviderItems.length > 0) {
      provider = internetOnlyProviderItems[0].provider;
    }

    if (!provider) {
      return false;
    }
    return provider;
  };

  const handleChange = (e, index) => {
    const inputValue = e.target.value;

    if (inputValue.length > 1) {
      const autoFilledData = inputValue.slice(0, length);

      onChange(autoFilledData);

      const focusIndex = Math.min(autoFilledData.length, length - 1);
      if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex].focus();
      }
      return;
    }

    const newValue = inputValue.substring(inputValue.length - 1);

    const otpArray = (value || "").padEnd(length, " ").split("");
    otpArray[index] = newValue === "" ? " " : newValue;

    const newOtpString = otpArray.join("");

    onChange(newOtpString);
    const normalized = newOtpString.replace(/\s/g, "");
    if (normalized.length === length) {
      onComplete?.(normalized);
    }

    if (newValue !== "" && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const otpArray = value.split("");

      if (otpArray[index] && otpArray[index] !== " ") {
        otpArray[index] = " ";
        onChange(otpArray.join(""));
      } else if (index > 0) {
        otpArray[index - 1] = " ";
        onChange(otpArray.join(""));
        inputRefs.current[index - 1].focus();
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (index > 0) {
        inputRefs.current[index - 1].focus();
        inputRefs.current[index - 1].select();
      }
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      if (index < length - 1) {
        inputRefs.current[index + 1].focus();
        inputRefs.current[index + 1].select();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").slice(0, length);
    if (!/^[0-9]+$/.test(pastedData)) {
      return;
    }
    onChange(pastedData);

    if (pastedData.length === length) {
      onComplete?.(pastedData);
    }

    const focusIndex = Math.min(pastedData.length, length - 1);
    inputRefs.current[focusIndex].focus();
  };

  const timerRef = useRef(null);
  const getCodeResendTimeout = () => {
    return application && application.codeResendTimeout > 0
      ? application.codeResendTimeout
      : initialTimer;
  };

  const handleCountDown = (leftTime) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setButtonLeftTime(leftTime);
    setShowCaptcha(false);

    timerRef.current = setInterval(() => {
      setButtonLeftTime((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          setShowCaptcha(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const handleSendCode = async() => {
    try {
      const response = await sendCode(handleCaptchaShow() === false ? "none" : handleCaptchaShow().type, captchaToken, clientSecret, method, countryCode, ...onButtonClickArgs);
      if (response.status === "ok") {
        setHasSentCode(true);
        handleCountDown(getCodeResendTimeout());
        showMessage("success", i18next.t("user:Verification code sent"));
        setTimeout(() => {
          if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
          }
        }, 50);
        onSend();
      } else {
        setCaptchaImg("");
        setCaptchaToken("");
        setClientSecret("");
        setIsCaptchaFalse((prev) => prev + 1);
        showMessage("error", response.msg);
      }

    } catch (err) {
      setCaptchaImg("");
      setCaptchaToken("");
      setClientSecret("");
      setIsCaptchaFalse((prev) => prev + 1);
    }
  };

  const prevReloadCount = useRef(shouldReloadCaptcha);

  useEffect(() => {
    if (shouldReloadCaptcha !== prevReloadCount.current) {
      prevReloadCount.current = shouldReloadCaptcha;
      setCaptchaImg("");
      setCaptchaToken("");
      setClientSecret("");
      setHasSentCode(false);
      setShowCaptcha(true);
      setButtonLeftTime(0);
    }
  }, [shouldReloadCaptcha]);

  useEffect(() => {
    if ("OTPCredential" in window) {
      const abortController = new AbortController();

      navigator.credentials.get({
        otp: {transport: ["sms"]},
        signal: abortController.signal,
      }).then(otp => {
        onChange(otp.code);
      }).catch(err => {
        console.log("Web OTP API Error:", err);
      });

      return () => {
        abortController.abort();
      };
    }
  }, []);

  useEffect(() => {
    if (!handleCaptchaShow() && !hasSentCode) {
      handleSendCode();
    }
  }, [hasSentCode]);

  return (
    <>
      <style>
        {`
          .fade-in-down {
            animation: fadeInDown 0.4s ease-out forwards;
            opacity: 0;
            transform: translateY(-10px);
          }
          .fade-out-up {
            animation: fadeOutUp 0.4s ease-out forwards;
            opacity: 1;
            transform: translateY(0);
          }
          @keyframes fadeInDown {
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes fadeOutUp {
            to {
              opacity: 0;
              transform: translateY(-10px);
              display: none;
            }
          }
        `}
      </style>

      <div className={showCaptcha ? "fade-in-down" : "fade-out-up"}
        style={{display: (showCaptcha && handleCaptchaShow()) ? "block" : "none"}}>
        <RsoCaptchaSmartLogin
          isCaptchaFalse={isCaptchaFalse}
          owner={application?.owner}
          name={application?.name}
          setCaptchaToken={(value) => setCaptchaToken(value)}
          setCaptchaImg={(value) => setCaptchaImg(value)}
          setClientSecret={(value) => setClientSecret(value)}
          captchaImg={captchaImg}
          captchaToken={captchaToken}
          isCurrentProvider={false}
        />
      </div>

      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "16px",
        direction: "rtl",
        minHeight: "100px",
      }}>
        {!hasSentCode ? (
          handleCaptchaShow() && (
            <Button
              id="send-otp"
              disabled={Boolean(handleCaptchaShow()) && captchaToken.length !== 5}
              style={{width: "100%"}}
              type="primary"
              size="large"
              onClick={handleSendCode}
            >
              {i18next.t("general:otp")}
            </Button>
          )
        ) : (
          <div className="fade-in-down"
            style={{display: "flex", flexDirection: "column", alignItems: "center", width: "100%"}}>
            <div style={{display: "flex", gap: "8px", flexDirection: "row-reverse", marginBottom: "16px"}}>
              {Array.from({length}).map((_, index) => (
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={value[index] === " " || !value[index] ? "" : toPersianDigits(value[index])}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onPaste={handlePaste}
                  maxLength={length}
                  style={{
                    width: "48px",
                    height: "48px",
                    textAlign: "center",
                    fontSize: "20px",
                    borderRadius: "8px",
                  }}
                />
              ))}
            </div>

            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "16px",
                minHeight: "32px",
              }}
            >
              {buttonLeftTime > 0 ? (
                <Text type="secondary" style={{display: "flex", alignItems: "center", gap: "8px", fontSize: "12px"}}>
                  <ClockCircleOutlined />
                  <div style={{color: themeData.colorPrimary}}>
                    {toPersianDigits(formatTime(buttonLeftTime))}
                  </div>
                  {i18next.t("general:More second until you receive a new code.")}
                </Text>
              ) : (
                <Button
                  id="resend-button"
                  size="large"
                  style={{width: "100%"}}
                  disabled={Boolean(handleCaptchaShow()) && captchaToken.length !== 5}
                  type="primary"
                  onClick={() => {
                    onChange(" ".repeat(length));
                    handleSendCode();
                  }}
                >
                  {i18next.t("general:Resend code")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

    </>
  );
};

export default RsoSendCodeOtp;
